/**
 * SenseAI — slim orchestrator (Phase 6 refactor).
 *
 * Owns sessions, providers, datasets, messages, builder/structured state +
 * side effects (load, send, save-to-gallery, budget-prompt fallback flow).
 * Delegates rendering to 4 children via `ctx` prop bags:
 *
 *   - SessionsSidebar     (left rail)
 *   - ChatPane            (header + welcome + messages + input)
 *   - BudgetPromptDialog  (cred-cap overlay)
 *   - StructuredBuilderDrawer (SA-only structured form, top-level mount —
 *      iter-119s fix: was previously misplaced inside StructuredView with
 *      undefined symbol references, never actually opened)
 */
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/lib/confirm-context";
import { useAuth } from "@/lib/auth-context";
import { useSettings } from "@/lib/settings-context";
import StructuredBuilderDrawer from "@/components/builder/StructuredBuilderDrawer";
import SessionsSidebar from "@/components/sense-ai/SessionsSidebar";
import ChatPane from "@/components/sense-ai/ChatPane";
import BudgetPromptDialog from "@/components/sense-ai/BudgetPromptDialog";

export default function SenseAI() {
    const { user } = useAuth();
    const { getAccessLevel } = useSettings();
    const senseAiLevel = getAccessLevel("sense_ai");
    const hasWrite = senseAiLevel === "W" || senseAiLevel === "A" || user?.role === "super_admin";
    const navigate = useNavigate();
    // iter-121.109 — Read dashboard_id / template_id from URL search params
    // so the Builder backend can inject the live widget list of THAT
    // specific dashboard/template into the LLM prompt. Set by
    // SenseAIBuilderFAB when the user clicks the FAB from a dashboard page.
    // iter-121.128d — Fall back to the LAST-VIEWED dashboard_id stashed in
    // sessionStorage by DashboardGrid. The previous behaviour required the
    // user to open Sense AI VIA the FAB on a specific dashboard page; if
    // they opened it from the sidebar (a very common path), no
    // dashboard_id was passed → no CURRENT DASHBOARD STATE block in the
    // prompt → the LLM hallucinated widget IDs and remove/update calls
    // failed. We only honour the fallback when the dashboard was viewed
    // recently (within the last 60 minutes) so a long-stale id doesn't
    // confuse a fresh editing session.
    const [searchParams] = useSearchParams();
    const builderContext = useMemo(() => {
        const ctx = {};
        const did = searchParams.get("dashboard_id");
        const tid = searchParams.get("template_id");
        if (did) ctx.dashboard_id = did;
        if (tid) ctx.template_id = tid;
        // sessionStorage fallback — only when the URL has neither.
        if (!did && !tid && typeof window !== "undefined") {
            try {
                const lastId = window.sessionStorage.getItem("senseai:lastDashboardId");
                const seenAtRaw = window.sessionStorage.getItem("senseai:lastDashboardSeenAt");
                const seenAt = seenAtRaw ? Number(seenAtRaw) : 0;
                const ageMin = (Date.now() - seenAt) / 60000;
                if (lastId && ageMin < 60) {
                    ctx.dashboard_id = lastId;
                }
            } catch (_e) { /* sessionStorage may be disabled — fail silent */ }
        }
        return ctx;
    }, [searchParams]);
    const isSuperAdmin = user?.role === "super_admin";
    const [sessions, setSessions] = useState([]);
    const [datasets, setDatasets] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    // Providers loaded dynamically from /api/sense-ai/providers (only enabled+configured)
    const [providers, setProviders] = useState([]);
    const [provider, setProvider] = useState("");
    // iter-119ar — multi-source selection. Now an array of IDs. Each entry
    // is either a raw dataset id OR a `model:<slug>` virtual id (canonical
    // model). Empty array = "No dataset context".
    const [datasetIds, setDatasetIds] = useState([]);
    const [budgetPrompt, setBudgetPrompt] = useState(null);
    // iter-121.86 — Once the personal credit cap is hit (and the tenant has
    // NOT explicitly opted into fallback-confirm via Settings → AI Budgets),
    // we lock the chat input until either (a) the user clicks the in-banner
    // "Try again" button — which auto-clears if the cap was raised — or
    // (b) the page reloads on a new calendar month. Carries the latest
    // {used, cap, message, scope} payload so the banner can render
    // contextual copy.
    const [budgetExhausted, setBudgetExhausted] = useState(null);
    // iter-121.87 — Soft "approaching cap" warning. Populated from
    // /home/widget/ai-usage on mount + after every chat send. When the
    // user's monthly spend hits `warn_threshold_pct` (default 80%) we
    // surface a dismissable banner ABOVE the input so the cap-hit dialog
    // is never the FIRST signal. Dismissal is session-scoped — a fresh
    // page load re-evaluates so the banner returns if usage grew.
    const [budgetWarn, setBudgetWarn] = useState(null);
    const [budgetWarnDismissed, setBudgetWarnDismissed] = useState(false);
    const refreshBudgetWarn = async () => {
        try {
            const r = await api.get("/home/widget/ai-usage");
            const d = r.data || {};
            const cap = Number(d.budget_credits || 0);
            const used = Number(d.used_credits || 0);
            const pct = Number(d.used_pct || 0);
            const warnAt = Number(d.warn_threshold_pct || 80);
            const hardAt = Number(d.hard_threshold_pct || 100);
            if (cap > 0 && pct >= warnAt && pct < hardAt) {
                setBudgetWarn({ used, cap, pct, warnAt });
            } else {
                setBudgetWarn(null);
            }
        } catch (_) { /* non-fatal */ }
    };
    useEffect(() => { refreshBudgetWarn(); }, []);
    // Builder mode — chats route to /api/sense-ai-builder/chat and render
    // tool-call cards. Persisted per-user so toggling survives reload.
    const [builderMode, setBuilderMode] = useState(() => {
        try { return localStorage.getItem("analyzio_builder_mode") === "1"; }
        catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem("analyzio_builder_mode", builderMode ? "1" : "0"); } catch { }
    }, [builderMode]);
    // iter-116d — SA-only structured-builder drawer
    const [structuredOpen, setStructuredOpen] = useState(false);
    // Added comments stating the issue resolved by this change:
    // Dynamically manage gallery items from database to maintain accurate saved indicators, prevent duplicate saves,
    // and resolve item IDs to support deep-linking "Open in Gallery". Also track load state to auto-restore active sessions.
    const [galleryItems, setGalleryItems] = useState([]);
    const [sessionsLoaded, setSessionsLoaded] = useState(false);
    const [providersLoaded, setProvidersLoaded] = useState(false);

    const loadGallery = useCallback(async () => {
        try {
            const r = await api.get("/gallery");
            setGalleryItems(r.data || []);
        } catch (_) {
            setGalleryItems([]);
        }
    }, []);

    const savedKeys = useMemo(() => {
        const s = new Set();
        for (const item of galleryItems) {
            const sourceKey = item.config?.source_key;
            if (sourceKey) {
                s.add(sourceKey);
            }
        }
        return s;
    }, [galleryItems]);

    const galleryKeysMap = useMemo(() => {
        const m = new Map();
        for (const item of galleryItems) {
            const sourceKey = item.config?.source_key;
            if (sourceKey) {
                m.set(sourceKey, item.id);
            }
        }
        return m;
    }, [galleryItems]);
    const bottomRef = useRef(null);
    const confirm = useConfirm();

    // iter-121.87 — Attach-file flow. The chat's paperclip button kicks off
    // a chunked upload (any size — no client-side caps; plan caps still
    // apply server-side via /api/data/uploads/init), polls the BG ingest
    // job, then auto-binds the newly-created dataset onto the chat context
    // so the user can immediately ask questions about it. Multiple files
    // can be uploaded in sequence and they all stack in datasetIds.
    const [attaching, setAttaching] = useState(null);
    // ^ shape: { name, pct, stage } | null
    const attachFile = async (file) => {
        if (!file) return;
        setAttaching({ name: file.name, pct: 0, stage: "Starting upload" });
        try {
            const { chunkedUpload, pollJob } = await import("@/lib/chunkedUpload");
            const { jobId } = await chunkedUpload(file, {
                onProgress: (pct) => setAttaching((s) => ({ ...(s || {}), name: file.name, pct, stage: "Uploading" })),
            });
            setAttaching((s) => ({ ...(s || {}), pct: 88, stage: "Importing" }));
            const finalJob = await pollJob(jobId, {
                onTick: (j) => setAttaching((s) => ({
                    ...(s || {}),
                    pct: Math.max(88, Number(j?.progress_pct || 88)),
                    stage: j?.stage_message || j?.status || "Importing",
                })),
            });
            if (finalJob?.status === "done" && finalJob?.dataset_id) {
                await loadDatasets();
                setDatasetIds((prev) => prev.includes(finalJob.dataset_id) ? prev : [...prev, finalJob.dataset_id]);
                toast.success(`${file.name} attached`, {
                    description: `Ready to query · ${Number(finalJob.row_count || 0).toLocaleString()} rows`,
                    duration: 6000,
                });
            } else {
                toast.error(`Upload failed: ${file.name}`, {
                    description: finalJob?.stage_message || finalJob?.error || "Background job did not complete.",
                    duration: 9000,
                });
            }
        } catch (e) {
            toast.error(`Upload failed: ${file.name}`, {
                description: e?.message || String(e).slice(0, 200),
                duration: 9000,
            });
        } finally {
            setAttaching(null);
        }
    };

    const loadProviders = useCallback(async () => {
        try {
            const r = await api.get("/sense-ai/providers");
            const items = (r.data || []).map((p) => ({
                value: `${p.provider}:${p.model}`,
                // iter-2026.02i — Prefer the SA-configured alias from
                // Settings → Administration → LLM Providers. Falls
                // back to the canned "{label} — {model}" only when
                // no alias is set.
                label: p.alias ? p.alias : `${p.label} — ${p.model}`,
                provider: p.provider,
                model: p.model,
                // iter-121.40 — preserve the source hint so the ChatHeader
                // can render a "Using platform default" pill for OAs who
                // are inheriting the SA fallback key.
                source: p.source || "tenant",
            }));
            setProviders(items);
            setProvider((curr) => curr || items[0]?.value || "");
        } catch (_) {
            setProviders([]);
        } finally {
            // Added comments stating the issue resolved by this change: track load state to safely trigger conversation auto-restoring.
            setProvidersLoaded(true);
        }
    }, []);
    const loadSessions = useCallback(async () => {
        try {
            const r = await api.get("/sense-ai/sessions");
            setSessions(r.data);
        } catch (_) {
            setSessions([]);
        } finally {
            // Added comments stating the issue resolved by this change: track load state to safely trigger conversation auto-restoring.
            setSessionsLoaded(true);
        }
    }, []);
    const loadDatasets = useCallback(async () => {
        const r = await api.get("/data/datasets");
        // iter-119aq — also surface canonical data models (marketing / sales /
        // finance) as selectable context. They show up under a "Canonical
        // models" group in the picker; selecting one routes the chat through
        // the model-summary code path instead of the dataset path. The id is
        // namespaced `model:<slug>` so we can disambiguate downstream.
        // iter-119as — RBAC: canonical models hold cross-tenant aggregates
        // and reveal platform schema decisions (synonyms, field types).
        // Restrict the picker entries to super_admin. Non-SA users only see
        // their own datasets — `tenant_scope_filter` on `/data/datasets`
        // already enforces tenant isolation server-side.
        let canonical = [];
        if (isSuperAdmin) {
            try {
                const mr = await api.get("/data-models");
                canonical = (mr.data || []).map((m) => ({
                    id: `model:${m.slug}`,
                    name: `${m.name || m.slug} (unified)`,
                    _canonical: true,
                    _slug: m.slug,
                }));
            } catch (_) {
                canonical = [];
            }
        }
        setDatasets([...canonical, ...(r.data || [])]);
    }, [isSuperAdmin]);
    useEffect(() => { loadProviders(); loadSessions(); loadDatasets(); loadGallery(); }, [loadProviders, loadSessions, loadDatasets, loadGallery]);
    // iter-121.154 — onboarding deep-link: preselect the dataset the user
    // just loaded in Step 1 so Sense AI opens with the data context set.
    // One-shot + only on a fresh chat (no active session to override).
    const _presetDatasetRef = useRef(false);
    useEffect(() => {
        if (_presetDatasetRef.current || active) return;
        const pre = searchParams.get("dataset_id");
        if (!pre || datasets.length === 0) return;
        if (datasets.some((d) => d.id === pre)) {
            setDatasetIds([pre]);
            _presetDatasetRef.current = true;
        }
    }, [datasets, searchParams, active]);
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const openSession = useCallback(async (s) => {
        setActive(s);
        // Added comments stating the issue resolved by this change: active conversation persistence across navigates / refreshes.
        try {
            localStorage.setItem("analyzio_sense_ai_last_session_id", s.id);
        } catch (_) { }
        // iter-119ar — hydrate multi-source selection from saved session.
        // Falls back to singular dataset_id / model_slug for back-compat.
        const ids = [];
        if (Array.isArray(s.dataset_ids)) ids.push(...s.dataset_ids);
        else if (s.dataset_id) ids.push(s.dataset_id);
        if (Array.isArray(s.model_slugs)) ids.push(...s.model_slugs.map((sl) => `model:${sl}`));
        else if (s.model_slug) ids.push(`model:${s.model_slug}`);
        setDatasetIds(ids);
        setBuilderMode(s.mode === "builder");
        const p = providers.find((x) => x.provider === s.provider && x.model === s.model);
        if (p) setProvider(p.value);
        const r = await api.get(`/sense-ai/sessions/${s.id}/messages`);
        // Builder-mode assistant messages store a JSON envelope in `content`;
        // hydrate them so the tool-call cards render on history replay.
        const hydrated = (r.data || []).map((m) => {
            if (m.mode !== "builder" || m.role !== "assistant") return m;
            try {
                const env = JSON.parse(m.content || "{}");
                return {
                    ...m,
                    content: env.message || "",
                    tool_calls: env.tool_calls || [],
                    tool_results: env.tool_results || [],
                };
            } catch { return m; }
        });
        setMessages(hydrated);
    }, [providers]);

    // Added comments stating the issue resolved by this change: restore last active session on initial load of both sessions and providers
    useEffect(() => {
        if (sessionsLoaded && providersLoaded && !active) {
            const lastSessionId = localStorage.getItem("analyzio_sense_ai_last_session_id");
            if (lastSessionId) {
                const found = sessions.find((s) => s.id === lastSessionId);
                if (found) {
                    openSession(found);
                }
            }
        }
    }, [sessionsLoaded, providersLoaded, sessions, active, openSession]);

    const newSession = () => {
        setActive(null);
        setMessages([]);
        // Added comments stating the issue resolved by this change: reset persisted conversation on new session click.
        try {
            localStorage.removeItem("analyzio_sense_ai_last_session_id");
        } catch (_) { }
    };

    const del = async (s) => {
        const ok = await confirm({
            title: `Delete "${s.title || 'this conversation'}"?`,
            description: "This permanently removes the conversation and all of its messages. This cannot be undone.",
            confirmLabel: "Delete conversation",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.delete(`/sense-ai/sessions/${s.id}`);
            toast.success("Conversation deleted");
            if (active?.id === s.id) {
                setActive(null);
                setMessages([]);
                // Added comments stating the issue resolved by this change: reset persisted conversation if the active one is deleted.
                try {
                    localStorage.removeItem("analyzio_sense_ai_last_session_id");
                } catch (_) { }
            }
            loadSessions();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const delAll = async () => {
        if (sessions.length === 0) return;
        const ok = await confirm({
            title: `Delete all ${sessions.length} conversation${sessions.length === 1 ? "" : "s"}?`,
            description: "This permanently removes every Sense AI conversation you own and all of their messages. This cannot be undone.",
            confirmLabel: "Delete all",
            destructive: true,
        });
        if (!ok) return;
        try {
            const r = await api.delete("/sense-ai/sessions", { data: { all: true } });
            const n = r?.data?.deleted ?? sessions.length;
            toast.success(`${n} conversation${n === 1 ? "" : "s"} deleted`);
            setActive(null);
            setMessages([]);
            // Added comments stating the issue resolved by this change: reset persisted conversation on delete all.
            try {
                localStorage.removeItem("analyzio_sense_ai_last_session_id");
            } catch (_) { }
            loadSessions();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Bulk delete failed");
        }
    };

    const send = async () => {
        if (!input.trim() || loading) return;
        const msg = input.trim();
        setInput("");
        if (builderMode) return _sendBuilder(msg);
        return _sendChat(msg);
    };

    const _sendBuilder = async (msg) => {
        const startTime = Date.now();
        const userMsg = {
            id: "tmp-" + Date.now(),
            session_id: active?.id,
            role: "user",
            content: msg,
            mode: "builder",
            created_at: new Date().toISOString(),
        };
        setMessages((m) => [...m, userMsg]);
        setLoading(true);
        try {
            const pm = providers.find((x) => x.value === provider);
            const payload = {
                session_id: active?.id,
                message: msg,
                provider: pm?.provider,
                model: pm?.model,
                // iter-121.109 — Merge URL-derived builder context (dashboard_id /
                // template_id) into the context payload so the backend can inject
                // the live widget list into the LLM prompt and surgical tool calls
                // target the right doc.
                context: {
                    ...(datasetIds.length ? { dataset_ids: datasetIds.filter((id) => !id.startsWith("model:")) } : {}),
                    // iter-2026 Option A — forward any explicitly selected model
                    // data-context so the backend can treat it as the
                    // authoritative binding (not just real datasets). Encoded
                    // as "model:<slug>" entries in datasetIds.
                    ...(datasetIds.some((id) => id.startsWith("model:"))
                        ? { model_slugs: datasetIds.filter((id) => id.startsWith("model:")).map((id) => id.slice("model:".length)) }
                        : {}),
                    ...builderContext,
                },
            };
            const r = await api.post("/sense-ai-builder/chat", payload);
            const d = r.data || {};
            const assistantMsg = {
                id: "ast-" + Date.now(),
                session_id: d.session_id,
                role: "assistant",
                content: d.message || "",
                mode: "builder",
                tool_calls: d.tool_calls || [],
                tool_results: d.tool_results || [],
                created_at: new Date().toISOString(),
                generation_time: ((Date.now() - startTime) / 1000).toFixed(2),
            };
            setMessages((m) => [...m, assistantMsg]);
            window.dispatchEvent(new Event("llm-spend-refresh"));
            // iter-121.87 — refresh "approaching cap" banner state.
            refreshBudgetWarn();
            if (!active && d.session_id) {
                const sr = await api.get("/sense-ai/sessions");
                setSessions(sr.data);
                const found = sr.data.find((s) => s.id === d.session_id);
                if (found) {
                    setActive(found);
                    // Added comments stating the issue resolved by this change: persist session ID when a new conversation is dynamically started.
                    try {
                        localStorage.setItem("analyzio_sense_ai_last_session_id", found.id);
                    } catch (_) { }
                }
            }
            // iter-66: persistent deep-link toast after save_as_* tool succeeds.
            const calls = d.tool_calls || [];
            const results = d.tool_results || [];
            for (let i = 0; i < calls.length; i++) {
                const t = calls[i]?.tool;
                const res = results[i];
                if ((t === "save_as_personal_dashboard" || t === "save_as_template") && res?.ok) {
                    const inner = res.result || res;
                    const dashId = inner.instantiated_dashboard_id || inner.dashboard_id;
                    const templateId = inner.template_id;
                    const version = inner.version || 1;
                    const isUpdate = version > 1;
                    const propName = (calls[i]?.args?.proposal?.name) || "Dashboard";
                    
                    const targetUrl = dashId ? `/dashboards/${dashId}` : (templateId ? `/dashboards/preview/${templateId}` : null);
                    const title = isUpdate ? "Dashboard Updated Successfully" : "Dashboard Created Successfully";
                    
                    toast.success(
                        <div className="flex flex-col gap-1 w-full" data-testid="dashboard-success-toast">
                            <span className="font-semibold text-sm">{title}</span>
                            {targetUrl ? (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(targetUrl);
                                    }}
                                    className="text-xs text-primary underline hover:text-primary/80 font-medium text-left truncate w-fit block"
                                    title={`Open ${propName}`}
                                    data-testid="toast-dashboard-link"
                                >
                                    {propName}
                                </button>
                            ) : (
                                <span className="text-xs text-muted-foreground">{propName}</span>
                            )}
                        </div>,
                        {
                            duration: 12000,
                            action: targetUrl ? {
                                label: "Open Dashboard",
                                onClick: () => navigate(targetUrl),
                            } : undefined,
                        },
                    );
                }
            }
        } catch (e) {
            // iter-121.81 — Surface the structured personal/tenant budget-hit 429
            // BEFORE falling through to the generic keyword matcher. Backend
            // sends the dict at top level (api_response.py); also accept `.detail`
            // shape for resilience.
            const body = e?.response?.data;
            const dStruct = (body && typeof body === "object" && body.detail && typeof body.detail === "object")
                ? body.detail
                : body;
            if (e?.response?.status === 429 && typeof dStruct === "object" && dStruct?.code === "personal_budget_hit") {
                const behavior = dStruct.fallback_behavior || "block";
                const fb = dStruct.fallback;
                // iter-121.86 — Default behaviour is now BLOCK: lock the input
                // until the cap resets (or an admin raises it). The legacy
                // "Continue with cheaper model" dialog only fires when the
                // tenant has explicitly set fallback_behavior=confirm via
                // Settings → AI Budgets.
                if (behavior === "auto" && fb?.provider && fb?.model) {
                    toast.info(`Personal credit cap hit — auto-switching to ${fb.model}`);
                    toast.error(dStruct.message || "Personal AI credit budget hit.");
                } else if (behavior === "confirm" && fb?.provider) {
                    setBudgetPrompt({
                        used: dStruct.user_used_credits,
                        cap: dStruct.user_cap_credits,
                        fallback: fb,
                        onConfirm: () => setBudgetPrompt(null),
                        onCancel: () => setBudgetPrompt(null),
                    });
                } else {
                    // BLOCK — lock the chat input.
                    setBudgetExhausted({
                        scope: "personal",
                        used: dStruct.user_used_credits,
                        cap: dStruct.user_cap_credits,
                        message: dStruct.message || "You've hit your personal AI credit budget.",
                    });
                }
                setMessages((mm) => mm.filter((x) => x.id !== userMsg.id));
                return;
            }
            if (e?.response?.status === 429 && typeof dStruct === "object" && dStruct?.code === "tenant_budget_hit") {
                // Tenant cap is always BLOCK — same banner UX.
                setBudgetExhausted({
                    scope: "tenant",
                    used: dStruct.tenant_used_credits,
                    cap: dStruct.tenant_cap_credits,
                    message: dStruct.message || "Your organisation's AI credit budget is exhausted.",
                });
                setMessages((mm) => mm.filter((x) => x.id !== userMsg.id));
                return;
            }
            if (e?.response?.status === 400 && typeof dStruct === "object" && dStruct?.code === "unsupported_data_context") {
                // Stage 3 — selection rejected before any AI call.
                toast.error("Can't use this data context", { description: dStruct.message });
                setMessages((mm) => mm.filter((x) => x.id !== userMsg.id));
                return;
            }
            // iter-109: surface actionable error messages (string-detail fallthrough).
            const rawDetail = (typeof body?.detail === "string") ? body.detail
                : (typeof body?.message === "string") ? body.message
                    : (e?.message || "");
            const lower = rawDetail.toLowerCase();
            let m = "Builder chat failed";
            let description = rawDetail || "Please retry — if this keeps happening, contact your admin.";
            if (lower.includes("invalid x-api-key") || lower.includes("authenticationerror") || lower.includes("invalid api key")) {
                m = "LLM API key invalid";
                description = "Your provider rejected the configured key. Open Settings → LLM Providers and rotate the key for your active provider.";
            } else if (lower.includes("rate limit") || lower.includes("ratelimit") || lower.includes("429")) {
                m = "LLM rate-limited — please retry";
                description = "The provider is throttling requests. Wait a few seconds and resend.";
            } else if (lower.includes("budget") || lower.includes("quota") || lower.includes("insufficient") || lower.includes("credits this month")) {
                m = "Out of LLM budget";
                description = "Top up credits in Billing or raise the spend cap in Settings → Budgets.";
            } else if (lower.includes("timeout") || lower.includes("econnaborted") || lower.includes("network")) {
                m = "Network or timeout";
                description = "Check your connection and retry. Long prompts can also time out — try shortening it.";
            }
            toast.error(m, { description, duration: 9000 });
            setMessages((mm) => mm.filter((x) => x.id !== userMsg.id));
        } finally {
            setLoading(false);
        }
    };

    const _sendChat = async (msg, { fallback } = {}) => {
        const startTime = Date.now();
        const pm = providers.find((x) => x.value === provider);
        if (!pm) {
            toast.error("No LLM provider is enabled and configured. Ask your admin to set one up in Settings → LLM Providers.");
            return;
        }
        const userMsg = {
            id: "tmp-" + Date.now(), session_id: active?.id,
            role: "user", content: msg, created_at: new Date().toISOString(),
        };
        if (!fallback) setMessages((m) => [...m, userMsg]);
        setLoading(true);
        try {
            const payload = {
                session_id: active?.id,
                message: msg,
                provider: fallback?.provider || pm.provider,
                model: fallback?.model || pm.model,
            };
            // iter-119ar — route to multi-source backend.
            const rawDatasetIds = datasetIds.filter((id) => !id.startsWith("model:"));
            const modelSlugs = datasetIds.filter((id) => id.startsWith("model:"))
                .map((id) => id.slice("model:".length));
            if (rawDatasetIds.length) payload.dataset_ids = rawDatasetIds;
            if (modelSlugs.length) payload.model_slugs = modelSlugs;
            if (fallback) payload.fallback_accepted = true;
            const r = await api.post("/sense-ai/chat", payload);
            const timeTaken = ((Date.now() - startTime) / 1000).toFixed(2);
            const assistantMsg = {
                ...(r.data.message || {}),
                generation_time: timeTaken,
            };
            setMessages((m) => [...m.filter((x) => x.id !== userMsg.id || fallback), userMsg, assistantMsg]
                .filter((x, i, a) => a.findIndex((y) => y.id === x.id) === i));
            // Live-refresh the TopBar LLM spend chip immediately (no 60s wait).
            window.dispatchEvent(new Event("llm-spend-refresh"));
            // iter-121.87 — refresh "approaching cap" banner state.
            refreshBudgetWarn();
            if (!active) {
                const sr = await api.get("/sense-ai/sessions");
                setSessions(sr.data);
                const found = sr.data.find((s) => s.id === r.data.session_id);
                if (found) {
                    setActive(found);
                    // Added comments stating the issue resolved by this change: persist session ID when a new conversation is dynamically started.
                    try {
                        localStorage.setItem("analyzio_sense_ai_last_session_id", found.id);
                    } catch (_) { }
                }
            }
        } catch (e) {
            // iter-121.81 — The backend exception handler (api_response.py) sends
            // structured HTTPException bodies at the TOP level of the response
            // (e.g. {code, message, user_used_credits, ...}), NOT nested under
            // a `detail` key. Some older tests + axios clients still see `detail`.
            // Accept BOTH shapes here so the budget-hit dialog actually surfaces
            // instead of falling through to "Request failed".
            const body = e?.response?.data;
            const detail = (body && typeof body === "object" && body.detail && typeof body.detail === "object")
                ? body.detail
                : body;
            // Personal credit budget hit — block/confirm/auto per server policy.
            if (e?.response?.status === 429 && typeof detail === "object" && detail?.code === "personal_budget_hit") {
                const behavior = detail.fallback_behavior || "block";
                const fb = detail.fallback;
                // iter-121.86 — Default behaviour is now BLOCK: lock the
                // input until budget is restored. Only tenants who opted
                // into `confirm` via Settings → AI Budgets see the dialog.
                if (behavior === "auto" && fb?.provider && fb?.model) {
                    toast.info(`Personal credit cap hit — auto-switching to ${fb.model}`);
                    return _sendChat(msg, { fallback: fb });
                }
                if (behavior === "confirm" && fb?.provider) {
                    setBudgetPrompt({
                        used: detail.user_used_credits,
                        cap: detail.user_cap_credits,
                        fallback: fb,
                        onConfirm: () => { setBudgetPrompt(null); _sendChat(msg, { fallback: fb }); },
                        onCancel: () => { setBudgetPrompt(null); setMessages((m) => m.filter((x) => x.id !== userMsg.id)); },
                    });
                    return;
                }
                // BLOCK — lock the input. The user sees a persistent banner
                // and the send/textarea are disabled until they click
                // "Try again" (which auto-clears if the cap was raised
                // or the next month's allotment kicked in).
                setBudgetExhausted({
                    scope: "personal",
                    used: detail.user_used_credits,
                    cap: detail.user_cap_credits,
                    message: detail.message || "You've hit your personal AI credit budget.",
                });
                setMessages((m) => m.filter((x) => x.id !== userMsg.id));
                return;
            }
            if (e?.response?.status === 429 && typeof detail === "object" && detail?.code === "tenant_budget_hit") {
                // Tenant cap is always BLOCK — same banner UX.
                setBudgetExhausted({
                    scope: "tenant",
                    used: detail.tenant_used_credits,
                    cap: detail.tenant_cap_credits,
                    message: detail.message || "Your organisation's AI credit budget is exhausted.",
                });
                setMessages((m) => m.filter((x) => x.id !== userMsg.id));
                return;
            }
            toast.error(typeof detail === "string" ? detail : (detail?.message || "Request failed"));
            setMessages((m) => m.filter((x) => x.id !== userMsg.id));
        } finally {
            setLoading(false);
        }
    };

    const saveToGallery = async (content, structured, savedKey) => {
        try {
            // Added comments stating the issue resolved by this change:
            // Include source_key in config so that saves hold state dynamically across refreshes and can map back to database gallery IDs.
            const res = await api.post("/gallery", {
                name: structured?.summary ? structured.summary.slice(0, 60) : "AI insight",
                kind: "insight",
                category: "Sense AI",
                description: content.slice(0, 200),
                config: {
                    structured,
                    source_key: savedKey,
                },
            });
            if (res.data && res.data.id) {
                setGalleryItems((prev) => [res.data, ...prev]);
            }
            toast.success("Saved insight to gallery");
        } catch { toast.error("Failed to save"); }
    };

    const saveKpiToGallery = async (kpi, savedKey) => {
        try {
            // Added comments stating the issue resolved by this change:
            // Include source_key in config so that saves hold state dynamically across refreshes and can map back to database gallery IDs.
            const res = await api.post("/gallery", {
                name: kpi.label || "KPI",
                kind: "kpi",
                category: "Sense AI",
                description: `Sense AI · ${kpi.value}${kpi.unit || ""}`,
                // Widget-compatible config — matches KpiWidget props on Dashboards
                config: {
                    value: typeof kpi.value === "number" ? kpi.value : Number(String(kpi.value).replace(/[^\d.-]/g, "")) || kpi.value,
                    unit: kpi.unit || "",
                    delta: typeof kpi.delta === "number" ? kpi.delta : undefined,
                    source_key: savedKey,
                },
            });
            if (res.data && res.data.id) {
                setGalleryItems((prev) => [res.data, ...prev]);
            }
            toast.success(`Saved "${kpi.label}" as widget`);
        } catch { toast.error("Failed to save KPI"); }
    };

    const saveChartToGallery = async (chart, savedKey) => {
        try {
            // Added comments stating the issue resolved by this change:
            // Include source_key in config so that saves hold state dynamically across refreshes and can map back to database gallery IDs.
            // iter-121.79 — Normalize the chart payload so the widget
            // renderer (Widgets.jsx ChartWidget) finds the right keys.
            //
            // The LLM emits two shapes:
            //   • Multi-measure: data=[{label, ad_spend, clicks}], series=[{key:"ad_spend"}, {key:"clicks"}]
            //   • Single-measure: data=[{label, value}] (no series, key="value" implied)
            //
            // BUG it fixes: ChartWidget renders bars with `dataKey="value"`
            // when there are no `groups`. When the LLM emits a single
            // metric using a NAMED key (e.g. `ad_spend`) without an
            // accompanying `series` field, the chart in chat rendered fine
            // (StructuredView falls back to key="value"), but the saved
            // gallery widget shows flat-zero bars because `value` is
            // missing from data items. Normalizing here guarantees
            // canonical shape regardless of what the LLM produced.
            const data = Array.isArray(chart.data) ? chart.data : [];
            const series = Array.isArray(chart.series) ? chart.series : [];
            let config;
            if (series.length > 1) {
                // Multi-series: preserve metric keys + add `groups` so
                // ChartWidget's multi-bar branch fires.
                config = {
                    chart_type: chart.type || "bar",
                    data,
                    groups: series.map((s) => s.key).filter(Boolean),
                    series,
                    x_label: chart.x_label,
                    y_label: chart.y_label,
                };
            } else {
                // Single-series: ensure every item has a numeric `value`
                // field even if the LLM used `ad_spend` / `count` / etc.
                const seriesKey = series[0]?.key;
                const normalized = data.map((d) => {
                    if (d && typeof d === "object" && (d.value !== undefined && d.value !== null && d.value !== "")) {
                        return d;  // already canonical
                    }
                    // Pick the LLM's series key first, then any numeric
                    // non-label field, then 0 as the last-resort fallback.
                    let v;
                    if (seriesKey && d?.[seriesKey] !== undefined) {
                        v = Number(d[seriesKey]);
                    } else {
                        const candidate = Object.entries(d || {})
                            .find(([k, val]) => k !== "label" && typeof val === "number");
                        v = candidate ? candidate[1] : 0;
                    }
                    return { ...d, value: Number.isFinite(v) ? v : 0 };
                });
                config = {
                    chart_type: chart.type || "bar",
                    data: normalized,
                    x_label: chart.x_label,
                    y_label: chart.y_label,
                };
            }
            const res = await api.post("/gallery", {
                name: chart.x_label || "Chart",
                kind: "chart",
                category: "Sense AI",
                description: `Sense AI · ${chart.type || "bar"} chart`,
                config: {
                    ...config,
                    source_key: savedKey,
                },
            });
            if (res.data && res.data.id) {
                setGalleryItems((prev) => [res.data, ...prev]);
            }
            toast.success("Saved chart as widget");
        } catch { toast.error("Failed to save chart"); }
    };

    const sidebarCtx = {
        sessions, setSessions, active, setActive, openSession, newSession, del, delAll, hasWrite,
    };
    const chatPaneCtx = {
        active, builderMode, setBuilderMode, isSuperAdmin, setStructuredOpen, hasWrite,
        datasetIds, setDatasetIds, datasets, provider, setProvider, providers,
        messages, loading, bottomRef,
        input, setInput, send,
        saveToGallery, saveKpiToGallery, saveChartToGallery,
        savedKeys,
        galleryKeysMap,
        // iter-121.86 — Locked-chat state. When non-null, the input + send
        // are disabled and the banner ABOVE the input renders.
        budgetExhausted,
        clearBudgetExhausted: () => setBudgetExhausted(null),
        // iter-121.87 — Soft "approaching cap" banner (dismissable).
        budgetWarn: (budgetWarn && !budgetWarnDismissed) ? budgetWarn : null,
        dismissBudgetWarn: () => setBudgetWarnDismissed(true),
        // iter-121.87 — File attach.
        attaching,
        attachFile,
    };
    if (senseAiLevel === "-") {
        return <Navigate to="/" replace />;
    }
    return (
        <div className="flex h-[calc(100vh-60px)]">
            <SessionsSidebar ctx={sidebarCtx} />
            <ChatPane ctx={chatPaneCtx} />
            <BudgetPromptDialog budgetPrompt={budgetPrompt} />
            {/* iter-119s fix: drawer mounted at the top-level so the
                'Structured' toolbar button (which sets structuredOpen) can
                actually open it. Previously this lived inside StructuredView
                with dangling references to undefined symbols. */}
            <StructuredBuilderDrawer
                open={structuredOpen}
                onClose={() => setStructuredOpen(false)}
                onInsertPrompt={(p) => {
                    setInput(p);
                    setStructuredOpen(false);
                    toast.success("Prompt inserted — press Send to generate the prebuilt");
                }}
            />
        </div>
    );
}
